import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { BookOpen, Clock, CheckCircle, Lock, PlayCircle, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { Course, Lesson } from '../types';
import { toast } from 'sonner';

export default function CoursePage() {
  const { courseId } = useParams();

  const { data: courseData, isLoading } = useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      const response = await api.get(`/education/courses/${courseId}`);
      return response.data.data.course as Course;
    },
  });

  const enrollMutation = useMutation({
    mutationFn: () => api.post(`/education/courses/${courseId}/enroll`),
    onSuccess: () => {
      toast.success('Enrolled successfully! Loading course...');
      setTimeout(() => { window.location.reload(); }, 1000);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Enrollment failed';
      toast.error(message);
    },
  });

  const course = courseData;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Course not found</h2>
          <Link to="/education" className="text-primary-600 hover:underline">Back to Education</Link>
        </div>
      </div>
    );
  }

  const completedLessons = course.lessons?.filter(l => l.isCompleted).length || 0;
  const totalLessons = course.lessons?.length || 0;
  const progress = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-8 text-white">
            <div className="mb-4">
              <Link to="/education" className="text-white/80 hover:text-white">← Back to Courses</Link>
            </div>
            <h1 className="text-4xl font-bold mb-4">{course.title}</h1>
            <p className="text-xl text-white/90 mb-6">{course.description}</p>
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center"><Clock className="w-5 h-5 mr-2" />{course.duration} minutes</div>
              <div className="flex items-center"><BookOpen className="w-5 h-5 mr-2" />{totalLessons} lessons</div>
              <div className="flex items-center"><Award className="w-5 h-5 mr-2" />{course.difficulty}</div>
            </div>
            {course.enrolled && (
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span>Your Progress</span>
                  <span>{progress.toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                  <div className="h-full bg-white transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
                <div className="mt-2 text-sm text-white/80">{completedLessons} of {totalLessons} lessons completed</div>
              </div>
            )}
          </div>

          <div className="p-8">
            {course.hasAccess && !course.enrolled ? (
              <button onClick={() => enrollMutation.mutate()} disabled={enrollMutation.isPending} className="btn btn-primary w-full md:w-auto">
                {enrollMutation.isPending ? 'Enrolling...' : 'Start Course'}
              </button>
            ) : course.hasAccess && course.enrolled ? (
              <div className="text-sm text-gray-600">✓ Enrolled - Continue learning below</div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-gray-900 font-medium">🔒 Upgrade Required</p>
                <p className="text-sm text-gray-700 mt-1">
                  This course requires {course.requiredTierUpgrade} tier or higher.
                </p>
                <button onClick={() => window.location.href = '/pricing'} className="btn btn-primary mt-3">View Plans</button>
              </div>
            )}
          </div>
        </motion.div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Content</h2>
          <div className="space-y-3">
            {course.lessons?.map((lesson: Lesson, index: number) => {
              const isLocked = !course.hasAccess;
              const isCompleted = lesson.isCompleted;
              return (
                <motion.div key={lesson.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}>
                  <Link
                    to={isLocked ? '#' : `/education/lessons/${lesson.id}`}
                    className={`block p-4 border rounded-lg transition ${isLocked ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-primary-500 hover:shadow-md'}`}
                    onClick={(e) => isLocked && e.preventDefault()}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${isCompleted ? 'bg-green-100' : isLocked ? 'bg-gray-100' : 'bg-primary-100'}`}>
                          {isCompleted ? <CheckCircle className="w-5 h-5 text-green-600" /> : isLocked ? <Lock className="w-5 h-5 text-gray-400" /> : <PlayCircle className="w-5 h-5 text-primary-600" />}
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-semibold ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>{index + 1}. {lesson.title}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{lesson.duration} min</span>
                            {lesson.hasQuiz && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Quiz</span>}
                          </div>
                        </div>
                      </div>
                      {isCompleted && <span className="text-xs text-green-600 font-medium">Completed</span>}
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
